// Compute the YYYY-MM-DD key for a game AS SEEN IN the user's selected TZ.
// Falls back to the backend-provided `game.date` when start_iso is missing.
export function gameDateKey(game, tz) {
  if (game?.start_iso) {
    try {
      const d = new Date(game.start_iso);
      if (!isNaN(d.getTime())) {
        const target = resolveTz(tz);
        // en-CA locale gives YYYY-MM-DD out of the box.
        const parts = new Intl.DateTimeFormat("en-CA", {
          timeZone: target,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).formatToParts(d);
        const y = parts.find((p) => p.type === "year")?.value;
        const m = parts.find((p) => p.type === "month")?.value;
        const dd = parts.find((p) => p.type === "day")?.value;
        if (y && m && dd) return `${y}-${m}-${dd}`;
      }
    } catch {
      // fall through
    }
  }
  const raw = game?.date;
  return raw && /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : "TBD";
}

// Group games by date, sort, format date headers, and TZ-aware time formatting.

export function groupGamesByDate(games, tz) {
  const groups = new Map(); // key: ISO date or "TBD"
  for (const g of games || []) {
    const key = gameDateKey(g, tz);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(g);
  }
  const entries = Array.from(groups.entries()).sort((a, b) => {
    if (a[0] === "TBD") return 1;
    if (b[0] === "TBD") return -1;
    return a[0].localeCompare(b[0]);
  });
  return entries;
}

export function formatDateHeader(key) {
  if (key === "TBD") return "DATE TBD";
  const d = new Date(key + "T12:00:00");
  if (isNaN(d.getTime())) return key;
  const dayOfWeek = d.toLocaleDateString("en-US", { weekday: "long" });
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const day = d.getDate();
  return `${dayOfWeek}, ${month} ${day}`.toUpperCase();
}

// Return the month-bucket key (YYYY-MM) for a game, computed in the user's TZ.
export function gameMonthKey(game, tz) {
  const k = gameDateKey(game, tz);
  if (!k || k === "TBD") return null;
  return k.slice(0, 7);
}

// Return the Monday-anchored YYYY-MM-DD week key, computed in the user's TZ.
export function gameWeekKey(game, tz) {
  const k = gameDateKey(game, tz);
  if (!k || k === "TBD") return null;
  return mondayOf(k);
}

function mondayOf(isoDate) {
  // Treat as local-noon to avoid TZ rollover when computing the day-of-week.
  const d = new Date(isoDate + "T12:00:00");
  if (isNaN(d.getTime())) return null;
  const day = d.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
  const diff = (day + 6) % 7; // distance back to Monday
  d.setDate(d.getDate() - diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function formatWeekLabel(key) {
  if (!key) return "Date TBD";
  const start = new Date(key + "T12:00:00");
  if (isNaN(start.getTime())) return key;
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${fmt(start)} – ${fmt(end)}`;
}

// ---- Quick-jump date filter helpers ---------------------------------------
// We compare game.date (already a YYYY-MM-DD string from the LLM extractor,
// expressed in the game's local-of-broadcast timezone) directly against
// today/tomorrow keys derived from the user's device timezone. For US-focused
// audiences this lines up: "Thu May 13" on a card vs "today is Thu May 13" on
// the device. It is intentionally not start_iso-aware — we want the schedule
// to feel like a paper edition, not a stopwatch.

function _localDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function todayKey() {
  return _localDateKey(new Date());
}

export function tomorrowKey() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return _localDateKey(d);
}

// Returns the YYYY-MM-DD keys for the *next* upcoming Sat & Sun (within 7 days).
// If today is Sat, returns [today, tomorrow]. If today is Sun, returns [today].
export function upcomingWeekendKeys() {
  const out = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const dow = d.getDay(); // 0=Sun, 6=Sat
    if (dow === 0 || dow === 6) out.push(_localDateKey(d));
    if (out.length === 2) break;
  }
  return out;
}

export function gameMatchesQuickFilter(game, quick, tz) {
  if (!quick) return true;
  const key = gameDateKey(game, tz);
  if (!key || key === "TBD") return false;
  if (quick === "today") return key === todayKey();
  if (quick === "tomorrow") return key === tomorrowKey();
  if (quick === "weekend") return upcomingWeekendKeys().includes(key);
  return true;
}

export function formatMonthLabel(key) {
  if (!key) return "Date TBD";
  const d = new Date(key + "-01T12:00:00");
  if (isNaN(d.getTime())) return key;
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

// Curated list of timezones — chosen to be common in a US-leaning sports app.
export const TIMEZONES = [
  { id: "auto", label: "Auto (your device)" },
  { id: "America/Los_Angeles", label: "Pacific (PT)" },
  { id: "America/Denver", label: "Mountain (MT)" },
  { id: "America/Chicago", label: "Central (CT)" },
  { id: "America/New_York", label: "Eastern (ET)" },
  { id: "Europe/London", label: "London (GMT/BST)" },
  { id: "Europe/Paris", label: "Central Europe (CET/CEST)" },
  { id: "Asia/Tokyo", label: "Tokyo (JST)" },
  { id: "Asia/Kolkata", label: "India (IST)" },
  { id: "Australia/Sydney", label: "Sydney (AEST/AEDT)" },
  { id: "UTC", label: "UTC" },
];

const TZ_ABBR = {
  "America/Los_Angeles": "PT",
  "America/Denver": "MT",
  "America/Chicago": "CT",
  "America/New_York": "ET",
  "Europe/London": "BST",
  "Europe/Paris": "CET",
  "Asia/Tokyo": "JST",
  "Asia/Kolkata": "IST",
  "Australia/Sydney": "AET",
  "UTC": "UTC",
};

function resolveTz(tz) {
  if (!tz || tz === "auto") {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }
  return tz;
}

export function shortTzAbbr(tz) {
  const t = resolveTz(tz);
  return TZ_ABBR[t] || t.split("/").pop().slice(0, 4).toUpperCase();
}

// Convert a game's time into the user-selected timezone.
// Returns "8:30 PM ET"-style string.
// Falls back to game.time if start_iso is missing/invalid.
// Short date for inline display inside a game card (next to the time).
// Example output: "MON OCT 5" or "DATE TBD".
export function formatShortDate(key) {
  if (!key || key === "TBD") return "DATE TBD";
  const d = new Date(key + "T12:00:00");
  if (isNaN(d.getTime())) return key;
  const dayOfWeek = d.toLocaleDateString("en-US", { weekday: "short" });
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const day = d.getDate();
  return `${dayOfWeek} ${month} ${day}`.toUpperCase();
}

export function formatGameTime(game, tz) {
  if (game?.start_iso) {
    const d = new Date(game.start_iso);
    if (!isNaN(d.getTime())) {
      try {
        const target = resolveTz(tz);
        const formatted = d.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          timeZone: target,
        });
        return `${formatted} ${shortTzAbbr(tz)}`;
      } catch {
        // fall through
      }
    }
  }
  return game?.time || "TIME TBD";
}
